import { createContext, useContext } from 'react'
import type { JSX } from 'react'
import type { ReactNode } from 'react'

import { useOrganizationSwitcher } from './useOrganizationSwitcher'
import type { UseOrganizationSwitcherReturn } from './useOrganizationSwitcher'

const OrganizationSwitcherContext =
  createContext<UseOrganizationSwitcherReturn | null>(null)

/**
 * Provider that shares organization switching state across components.
 *
 * This ensures that when an org switch is triggered, all consumers
 * (e.g., OrganizationSwitcher, MainSidebar skeleton) see the same loading state.
 */
export function OrganizationSwitcherProvider({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const organizationSwitcher = useOrganizationSwitcher()

  return (
    <OrganizationSwitcherContext.Provider value={organizationSwitcher}>
      {children}
    </OrganizationSwitcherContext.Provider>
  )
}

/**
 * Hook to access organization switching state from context.
 *
 * Must be used within an OrganizationSwitcherProvider.
 *
 * @example
 * ```tsx
 * const { loading, switchOrganization } = useOrganizationSwitcherContext()
 * ```
 */
export function useOrganizationSwitcherContext(): UseOrganizationSwitcherReturn {
  const context = useContext(OrganizationSwitcherContext)

  if (!context) {
    throw new Error(
      'useOrganizationSwitcherContext must be used within an OrganizationSwitcherProvider',
    )
  }

  return context
}
